'use client';
/**
 * LES SOCIÉTÉS ET LES TECHNICIENS D'UN ÉQUIPEMENT, RELIÉS À L'ANNUAIRE (Ines, 28/10/2026).
 *
 * Fournisseur, société du contrat de maintenance, société du contact technique et ses techniciens : des fiches de
 * l'annuaire, plus du texte libre. La référence fait foi ; le nom et le téléphone affichés sont relus dans l'annuaire,
 * là où on les tient à jour. Le moteur vérifie et réécrit les étiquettes : `services/contacts-equipement.ts`.
 *
 * Une saisie ANCIENNE sans référence s'affiche telle quelle, marquée « à relier » : on ne l'efface pas, c'est le seul
 * nom connu tant que personne n'a choisi la fiche.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { Pastille, Selecteur, inputStyle } from '@/components/ui';
import { ChoixCorrespondant, useNomsContacts, type ContactBref } from '@/components/ChoixCorrespondant';
import { Bouton } from '@/shared/Bouton';
import { Tableau } from '@/shared/Tableau';
import type { GedEquipement, ContactEquip } from '@/lib/types';
import { lbl } from './commun';

export const FAMILLES_SOCIETE = ['fournisseurs'] as const;

type Tel = { affichage: string; actif: boolean };
export type Interlocuteur = { per_id: string; nom_complet: string; fonction_libelle?: string; actif: boolean; telephones: Tel[] };
export type ContactTech = NonNullable<GedEquipement['contactTechnique']>;
export type LigneTech = ContactTech['techniciens'][number];

/** Les interlocuteurs d'une société de l'annuaire (actifs d'abord), avec leurs téléphones. `null` tant que non lus. */
export function useInterlocuteurs(orgId: string | undefined | null): Interlocuteur[] | null {
  const [etat, setEtat] = React.useState<{ id: string; liste: Interlocuteur[] | null }>({ id: '', liste: null });
  React.useEffect(() => {
    if (!orgId) return;
    let vivant = true;
    void fetch(`/api/ged/ged/annuaire/organisations/${encodeURIComponent(orgId)}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (vivant) setEtat({ id: orgId, liste: Array.isArray(j?.interlocuteurs) ? j.interlocuteurs : [] }); })
      .catch(() => { if (vivant) setEtat({ id: orgId, liste: [] }); });
    return () => { vivant = false; };
  }, [orgId]);
  if (!orgId) return [];
  return etat.id === orgId ? etat.liste : null;
}

const premierTel = (i: Interlocuteur | undefined) => i?.telephones?.find((t) => t.actif)?.affichage || '';

/** Ouvre la fiche de l'annuaire. */
function LienAnnuaire({ code, children }: { code: string; children: React.ReactNode }) {
  const { select, go } = useGed();
  return (
    <Bouton taille="sm" variante="discret" type="button" title="Ouvrir la fiche dans l’annuaire"
      onClick={() => { select('selectedAnnuaire', code); go('annuaire'); }}>
      {children}
    </Bouton>
  );
}

const ARelier = () => (
  <Pastille ton="warning" compacte title="Saisie ancienne, sans fiche de l’annuaire. Modifiez la fiche de l’équipement pour choisir la société dans l’annuaire.">
    à relier à l’annuaire
  </Pastille>
);

/** Une société de la fiche : son nom actuel dans l'annuaire (avec lien), ou la saisie ancienne « à relier ». */
export function SocieteAffichee({ code, texte }: { code?: string | null; texte?: string | null }) {
  const noms = useNomsContacts(code ? [code] : []);
  if (code) {
    const c = noms.get(code);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <LienAnnuaire code={code}>{c?.nom || texte || code}</LienAnnuaire>
        {c && !c.actif && <Pastille ton="muted" compacte>désactivée</Pastille>}
      </span>
    );
  }
  if (!texte) return null;
  return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{texte} <ARelier /></span>;
}

const lienTel = (tel: string) => (
  <a href={`tel:${tel.replace(/\s+/g, '')}`} style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-primary-active)', textDecoration: 'none' }}>{tel}</a>
);

/** Les techniciens du contact technique, lus dans l'annuaire quand ils y sont reliés. */
export function TechniciensAffiches({ ct }: { ct: ContactTech | null | undefined }) {
  const inter = useInterlocuteurs(ct?.societeCode);
  const techs = ct?.techniciens || [];
  if (!techs.length) return null;
  return (
    <ul style={{ margin: 0, paddingInlineStart: 16, display: 'grid', gap: 3 }}>
      {techs.map((t, i) => {
        const vivant = t.code ? inter?.find((x) => x.per_id === t.code) : undefined;
        const nom = vivant?.nom_complet || t.nom;
        const tel = (vivant && premierTel(vivant)) || t.tel;
        return (
          <li key={t.code || `l${i}`}>
            {t.code ? <LienAnnuaire code={t.code}>{nom || t.code}</LienAnnuaire> : nom}
            {vivant?.fonction_libelle && <span style={{ color: 'var(--text-muted)' }}> · {vivant.fonction_libelle}</span>}
            {tel && <> — {lienTel(tel)}</>}
            {t.code && inter && vivant && !vivant.actif && <> <Pastille ton="muted" compacte>n’est plus interlocuteur</Pastille></>}
            {!t.code && <> <ARelier /></>}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * SAISIE du contact technique : la société dans l'annuaire, puis ses techniciens parmi ses interlocuteurs.
 * Un technicien absent de l'annuaire s'ajoute d'abord à la fiche de la société — on ne recrée pas de carnet ici.
 */
export function ChoixContactTechnique({ valeur, onChange, prefixe }: {
  valeur: ContactTech; onChange: (v: ContactTech) => void; prefixe: string;
}) {
  const inter = useInterlocuteurs(valeur.societeCode);
  const pris = new Set(valeur.techniciens.map((t) => t.code).filter(Boolean));
  const offerts = (inter || []).filter((i) => i.actif && !pris.has(i.per_id));
  const retirer = (k: number) => onChange({ ...valeur, techniciens: valeur.techniciens.filter((_, j) => j !== k) });
  const ajouter = (perId: string) => {
    const i = inter?.find((x) => x.per_id === perId);
    if (!i) return;
    onChange({ ...valeur, techniciens: [...valeur.techniciens, { code: i.per_id, nom: i.nom_complet, tel: premierTel(i) }] });
  };
  const changerSociete = (code: string) => {
    /* Changer de société retire les techniciens RELIÉS à l'ancienne : ils sont ses interlocuteurs, pas ceux de la
       nouvelle. Les saisies anciennes restent, à relier ou à retirer. */
    onChange({ ...valeur, societeCode: code, techniciens: valeur.techniciens.filter((t) => !t.code) });
  };

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <div>
        <label style={lbl} htmlFor={`${prefixe}-soc`}>Société</label>
        <ChoixCorrespondant id={`${prefixe}-soc`} valeur={valeur.societeCode || ''} onChange={changerSociete}
          familles={FAMILLES_SOCIETE} vide="— aucune société"
          aide={!valeur.societeCode && valeur.societe
            ? <>Saisie ancienne : <strong>{valeur.societe}</strong> — choisissez la fiche correspondante dans l’annuaire.</>
            : 'Les fournisseurs de l’annuaire. Absente ? Créez-la d’abord dans l’annuaire.'} />
      </div>
      <div>
        <span style={lbl}>Techniciens</span>
        {valeur.techniciens.length > 0 && (
          <ul style={{ margin: '0 0 8px', padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
            {valeur.techniciens.map((t, k) => (
              <li key={t.code || `l${k}`} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
                <span style={{ color: 'var(--text-strong)', fontWeight: 600 }}>{t.nom || t.code}</span>
                {t.tel && <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{t.tel}</span>}
                {!t.code && <ARelier />}
                <Bouton taille="sm" variante="discret" type="button" onClick={() => retirer(k)}
                  title={`Retirer ${t.nom || t.code} du contact technique`}>Retirer</Bouton>
              </li>
            ))}
          </ul>
        )}
        {!valeur.societeCode ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Choisissez d’abord la société : ses techniciens sont ses interlocuteurs dans l’annuaire.</div>
        ) : inter === null ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture des interlocuteurs…</div>
        ) : (
          <>
            <Selecteur id={`${prefixe}-tech`} value="" onChange={ajouter} style={inputStyle} disabled={!offerts.length}>
              <option value="">{offerts.length ? '+ Ajouter un technicien' : 'Aucun autre interlocuteur actif'}</option>
              {offerts.map((i) => (
                <option key={i.per_id} value={i.per_id}>
                  {i.nom_complet}{i.fonction_libelle ? ` · ${i.fonction_libelle}` : ''}{premierTel(i) ? ` — ${premierTel(i)}` : ''}
                </option>
              ))}
            </Selecteur>
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
              Un technicien absent de la liste s’ajoute d’abord comme interlocuteur, sur la fiche de la société dans l’annuaire.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
 * UN SEUL BLOC « CONTACTS » (spec 002, récit 9 — 04/11/2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Les contacts existaient deux fois : la liste « Contacts » de la Mise en service
 * (nom, organisme, téléphone saisis à la main, `contacts` de la fiche) et le
 * « Contact technique » de l'Identification, relié à l'annuaire. La source est
 * désormais l'ANNUAIRE : fournisseur, société du contrat, société et techniciens
 * du contact technique. La liste saisie à la main reste LUE — rien n'est effacé —
 * chaque ligne marquée « à relier », avec la fiche de l'annuaire qui lui
 * correspond probablement. On ne saisit plus de contact à la main ici.
 */

export const ROLES_CONTACT: Record<ContactEquip['role'], string> = {
  fournisseur: 'Fournisseur', sav: 'SAV / technicien', referent: 'Référent interne', autre: 'Autre',
};

/** La fiche de l'annuaire qui correspond probablement à chaque contact saisi à la main
 *  (recherche sur l'organisme, à défaut sur le nom). `undefined` : pas encore lu ; `null` : rien trouvé. */
function usePropositions(contacts: ContactEquip[]): Map<string, ContactBref | null | undefined> {
  const requetes = React.useMemo(
    () => [...new Set(contacts.map((c) => String(c.organisme || c.nom || '').trim()).filter((q) => q.length >= 2))],
    [contacts],
  );
  const cle = requetes.join('\u0001');
  const [trouves, setTrouves] = React.useState<{ cle: string; carte: Map<string, ContactBref | null> }>({ cle: '', carte: new Map() });
  React.useEffect(() => {
    if (!cle) return;
    let vivant = true;
    void Promise.all(requetes.map(async (q) => {
      try {
        const r = await fetch(`/api/ged/ged/annuaire/contacts?q=${encodeURIComponent(q)}&familles=${encodeURIComponent(FAMILLES_SOCIETE.join(','))}`, { credentials: 'include' });
        const j = r.ok ? await r.json() as { contacts?: ContactBref[] } : null;
        return [q, (j?.contacts || []).find((c) => c.actif) || null] as const;
      } catch { return [q, null] as const; }
    })).then((paires) => { if (vivant) setTrouves({ cle, carte: new Map(paires) }); });
    return () => { vivant = false; };
  }, [cle, requetes]);
  return new Map(contacts.map((c) => {
    const q = String(c.organisme || c.nom || '').trim();
    return [c.id, trouves.cle === cle ? (trouves.carte.get(q) ?? null) : undefined];
  }));
}

export function BlocContacts({ e, onRelier }: {
  e: GedEquipement;
  /** Ouvre la saisie du contact technique, la société proposée déjà choisie. Absent : lecture seule. */
  onRelier?: (societeCode: string) => void;
}) {
  const ct = e.contactTechnique;
  /* ⚠️ `contacts` n'est une LISTE que sur les fiches qui en ont saisi : sur les
     152 postes informatiques, le champ porte un OBJET (usage, type de poste…),
     reliquat de la reprise. Le lire comme une liste faisait planter la
     Présentation de ces fiches (`.map` sur un objet). */
  const saisis = Array.isArray(e.contacts) ? e.contacts : [];
  const propositions = usePropositions(saisis);
  const rien = !(e.fournisseurCode || e.fournisseur) && !(e.contrat?.societeCode || e.contrat?.societe)
    && !(ct?.societeCode || ct?.societe) && !(ct?.techniciens || []).length && !saisis.length;
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px 18px', fontSize: 12.5 }}>
        <div><span style={lbl}>Fournisseur</span>{(e.fournisseurCode || e.fournisseur) ? <SocieteAffichee code={e.fournisseurCode} texte={e.fournisseur} /> : <Vide />}</div>
        <div><span style={lbl}>Société du contrat</span>{(e.contrat?.societeCode || e.contrat?.societe) ? <SocieteAffichee code={e.contrat?.societeCode} texte={e.contrat?.societe} /> : <Vide />}</div>
        <div><span style={lbl}>Contact technique</span>{(ct?.societeCode || ct?.societe) ? <SocieteAffichee code={ct?.societeCode} texte={ct?.societe} /> : <Vide />}</div>
        <div style={{ gridColumn: '1 / -1' }}><span style={lbl}>Techniciens</span>{ct?.techniciens?.length ? <TechniciensAffiches ct={ct} /> : <Vide />}</div>
      </div>
      {rien && (
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Aucun interlocuteur : à 7 h du matin, personne à joindre. Choisissez la société et ses techniciens dans l’annuaire.
        </div>
      )}
      {saisis.length > 0 && (
        <div style={{ display: 'grid', gap: 6 }}>
          <span style={lbl}>Saisis à la main avant l’annuaire — à relier</span>
          <Tableau
            id="contacts-saisis-equipement"
            ajusterLargeur
            lignes={saisis}
            cleLigne={(c) => c.id}
            vide={{ titre: 'Aucun contact saisi à la main.' }}
            colonnes={[
              { cle: 'role', libelle: 'Rôle', type: 'liste', valeur: (c) => ROLES_CONTACT[c.role] || c.role, rendu: (c) => ROLES_CONTACT[c.role] || c.role },
              { cle: 'nom', libelle: 'Nom', valeur: (c) => c.nom, rendu: (c) => <strong>{c.nom}</strong> },
              { cle: 'organisme', libelle: 'Organisme', valeur: (c) => c.organisme || '', rendu: (c) => c.organisme || '' },
              { cle: 'tel', libelle: 'Téléphone', valeur: (c) => c.telephone || '', rendu: (c) => (c.telephone ? lienTel(c.telephone) : '') },
              { cle: 'email', libelle: 'E-mail', valeur: (c) => c.email || '', rendu: (c) => c.email || '' },
              { cle: 'delai', libelle: 'Délai contractuel', valeur: (c) => c.delaiContractuel || '', rendu: (c) => c.delaiContractuel || '' },
              { cle: 'annuaire', libelle: 'Dans l’annuaire', style: { whiteSpace: 'normal' }, rendu: (c) => {
                const p = propositions.get(c.id);
                return (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <ARelier />
                    {p === undefined ? <span style={{ color: 'var(--text-muted)' }}>recherche…</span>
                      : p === null ? <span style={{ color: 'var(--text-muted)' }}>aucune fiche proche</span>
                        : <>
                          <span style={{ color: 'var(--text-muted)' }}>proposition :</span> <LienAnnuaire code={p.id}>{p.nom}</LienAnnuaire>
                          {onRelier && <Bouton taille="sm" onClick={() => onRelier(p.id)} title={`Choisir ${p.nom} comme société du contact technique`}>Relier</Bouton>}
                        </>}
                  </span>
                );
              } },
            ]}
          />
        </div>
      )}
    </div>
  );
}

const Vide = () => <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>non renseigné</div>;
